import moment from 'moment';
import React from 'react';
import { shallowEqual } from 'react-redux';
import Form from '../../form';
import { TFormVariant } from '../../form/Form.types';
import { Col } from '../../grid';
import { InputSkeletonProps } from '../components/input-skeleton';
import TextField from '../text-field';
import { DateFieldSC } from './date-field.styled';
import { AbstractDateFieldProps, IDateFieldHandle, IInputState } from './date-field.types';
import { dateToInnerState, defaultInputState, isDeleting } from './date-field.utils';
import useIsMounted from '@hooks/utils/useIsMounted';

interface DateFieldProps extends AbstractDateFieldProps {
  variant?: TFormVariant;
  /**
   * Input states
   */
  disabled?: boolean;
  readonly?: boolean;
  error?: boolean;
  borderLess?: InputSkeletonProps['borderLess'];
  style?: React.CSSProperties;
  className?: string;
  format?: string;
  value?: Date | null;

  onChange?: (date?: Date) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  blurOnFinishEditing?: boolean;
}

const DateField = (props: DateFieldProps, forwardedRef?: React.Ref<IDateFieldHandle | null>) => {
  const {
    style,
    disabled,
    readonly,
    error,
    className,
    borderLess,
    type,
    variant,
    onChange,
    onBlur,
    onFocus,
    value,
    name,
    blurOnFinishEditing = true,
    required,
    ...rest
  } = props;

  const isMounted = useIsMounted();
  const settings = Form.useFormSettings({ variant, disabled });

  const ref1 = React.useRef<HTMLInputElement>(null);
  const ref2 = React.useRef<HTMLInputElement>(null);
  const ref3 = React.useRef<HTMLInputElement>(null);

  const [inputsValues, setInputsValues] = React.useState<IInputState>(() => {
    return dateToInnerState(value);
  });

  React.useEffect(() => {
    if (!isMounted()) {
      return;
    }

    // if null parsed, reset field to default state
    if (value === null) {
      setInputsValues({ ...defaultInputState });
      return;
    } else if (value) {
      const parsed = dateToInnerState(value);

      if (!shallowEqual(parsed, inputsValues)) {
        setInputsValues(parsed);
      }
    }
  }, [value]);

  React.useImperativeHandle(
    forwardedRef,
    () => ({
      focus: () => {
        ref1.current?.blur();
      },
      blur: () => {
        ref1.current?.blur();
        ref2.current?.blur();
        ref3.current?.blur();
      },
    }),
    [],
  );

  const handleInputChange = (valueKey: keyof IInputState, nv: string) => {
    setInputsValues((v) => {
      const next = {
        ...v,
        [valueKey]: nv,
      };

      const fullDate = moment(`${next.year}-${next.month}-${next.day}`, 'YYYY-MM-DD', true);

      if (fullDate.isValid()) {
        onChange?.(fullDate.toDate());
      } else if (value && value instanceof Date) {
        onChange?.(undefined);
      }

      return next;
    });
  };

  return (
    <DateFieldSC gutter={16} wrap={'wrap'} className="ui-date-field">
      <Col xs={12} lg={3}>
        <TextField
          {...rest}
          ref={ref1}
          value={inputsValues.day}
          onChange={(e) => {
            handleInputChange('day', e.target.value);
          }}
          onKeyUp={(e) => {
            if (!isDeleting(e) && e.target.value.length === 2) {
              ref2.current?.focus();
            }
          }}
          disabled={settings.disabled}
          variant={settings.variant}
          readonly={readonly}
          error={error}
          onBlur={onBlur}
          onFocus={onFocus}
          name={name}
          placeholder="DD"
        />
      </Col>
      <Col xs={12} lg={3}>
        <TextField
          ref={ref2}
          value={inputsValues.month}
          onChange={(e) => {
            handleInputChange('month', e.target.value);
          }}
          onKeyUp={(e) => {
            if (!isDeleting(e) && e.target.value.length === 2) {
              ref3.current?.focus();
            }
          }}
          onKeyDown={(e) => {
            if (isDeleting(e) && inputsValues.month.length === 0) {
              ref1.current?.focus();
            }
          }}
          disabled={settings.disabled}
          variant={settings.variant}
          readonly={readonly}
          error={error}
          onBlur={onBlur}
          name={name}
          placeholder="MM"
        />
      </Col>
      <Col xs={12} lg={6}>
        <TextField
          ref={ref3}
          value={inputsValues.year}
          onChange={(e) => {
            handleInputChange('year', e.target.value);
          }}
          onKeyUp={(e) => {
            if (!isDeleting(e) && e.target.value.length === 4 && blurOnFinishEditing) {
              ref3.current?.blur();
            }
          }}
          onKeyDown={(e) => {
            if (isDeleting(e) && inputsValues.year.length === 0) {
              ref2.current?.focus();
            }
          }}
          disabled={settings.disabled}
          readonly={readonly}
          variant={settings.variant}
          error={error}
          onBlur={onBlur}
          name={name}
          placeholder="YYYY"
        />
      </Col>
    </DateFieldSC>
  );
};

export default React.forwardRef(DateField);
